import { Component, ViewEncapsulation, OnInit, AfterViewInit } from '@angular/core';
import { trigger, style, transition, animate } from '@angular/animations';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { OrderService } from 'src/app/services/order/order.service';
import { PaginatedResponse, PaginationInfo } from 'src/app/shared/models';
import { Proforma } from '../proforma.model';
import { ProformaModalService } from 'src/app/services/proforma-modal/proforma-modal.service';
import { ClientService } from 'src/app/services/user/client.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { UserService } from 'src/app/services/user/user.service';
import { BlAmendFormModalService } from 'src/app/services/bl-amend-form-modal/bl-amend-form-modal.service';
import { PortService } from 'src/app/services/port/port.service';
import { BankService } from 'src/app/services/bank/bank.service';
import { GlobalStateService } from 'src/app/services/global/global-state.service';
import { Router } from '@angular/router';

declare var KTMenu: any;
declare var bootstrap: any;

// Define filter interface
interface ProformaFilters {
  customer?: any;
  salesPerson?: any;
  customer_id?: string;
  sales_person_id?: string;
  proforma_no?: string;
  from_country?: string;
  from_country_label?: string;
  from_port?: string;
  to_country?: string;
  to_country_label?: string;
  to_port?: string;
  chassis_no?: string;
  bank?: string;
  stock_number?: string;
}

@Component({
  selector: 'app-proforma-list',
  templateUrl: './proforma-list.component.html',
  styleUrls: ['./proforma-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ height: '0px', opacity: 0, overflow: 'hidden' }),
        animate('300ms ease-in-out', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        style({ height: '*', opacity: 1, overflow: 'hidden' }),
        animate('300ms ease-in-out', style({ height: '0px', opacity: 0 }))
      ])
    ])
  ]
})
export class ProformaListComponent extends DestructorComponent implements OnInit, AfterViewInit {

  // Data properties
  proformas: Proforma[] = [];
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 20,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };
  loadBl: boolean = true;

  // State properties
  isLoading: boolean = false;
  searchTerm: string = '';
  currentPage: number = 1;

  // Filter properties
  showFilters: boolean = false;
  filters: ProformaFilters = {};

  // Customer search properties
  customerSearchTerm: string = '';
  selectedCustomer: any = null;
  customerSearchResults: any[] = [];
  showCustomerDropdown: boolean = false;
  isCustomerSearching: boolean = false;
  private customerSearchSubject = new Subject<string>();

  // Sales person search properties
  salesPersonSearchTerm: string = '';
  selectedSalesPerson: any = null;
  salesPersonSearchResults: any[] = [];
  showSalesPersonDropdown: boolean = false;
  isSalesPersonSearching: boolean = false;
  private salesPersonSearchSubject = new Subject<string>();

  // New filter properties
  countries: any[] = [];
  fromPorts: any[] = [];
  toPorts: any[] = [];
  banks: any[] = [];
  isLoadingFromPorts: boolean = false;
  isLoadingToPorts: boolean = false;

  constructor(
    private _orderService: OrderService,
    private _proformaModalService: ProformaModalService,
    private clientService: ClientService,
    private userService: UserService,
    private _blAmendFormModalService: BlAmendFormModalService,
    private _globalStateService: GlobalStateService,
    private portService: PortService,
    private bankService: BankService,
    private _router: Router
  ) {
    super();
    this.setupCustomerSearchDebounce();
    this.setupSalesPersonSearchDebounce();
  }

  ngOnInit(): void {
    this.loadProformas(1);
    this.loadCountries();
    this.loadBanks();
  }

  ngAfterViewInit(): void {
    // Initialize menus after view is initialized
    this.initializeMenus();
  }

  loadProformas(page: number = 1, search: string = '', filters: Record<string, any> = {}): void {
    this.isLoading = true;
    this.currentPage = page;

    let search_filters = {...filters};
    if (search_filters.customer) {
      delete search_filters.customer;
    }
    if (search_filters.salesPerson) {
      delete search_filters.salesPerson;
    }


    this.subscriptions.push(
      this._orderService.GetData(page.toString(), search, search_filters).subscribe({
        next: (response: PaginatedResponse<Proforma>) => {
          if (response.status && response.data) {
            this.proformas = response.data.data || [];
            this.paginationInfo = response.data.pagination;
            this.currentPage = response.data.pagination.currentPage;
          } else {
            this.proformas = [];
            this.resetPagination();
          }
          this.isLoading = false;
          // Initialize KTMenu instances after data is loaded
          this.refreshMenus();
        },
        error: (error) => {
          console.error('Error loading proformas:', error);
          this.proformas = [];
          this.resetPagination();
          this.isLoading = false;
          // Initialize KTMenu instances even on error
          this.refreshMenus();
        }
      })
    );
  }

  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 20,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  onPageChange(page: number): void {
    this.loadProformas(page, this.searchTerm);
  }

  onSearch(searchTerm: string): void {
    this.searchTerm = searchTerm;
    this.loadProformas(1, searchTerm);
  }

  // Helper methods for displaying data
  getTotalPrice(proforma: Proforma): string {
    if (proforma.items && proforma.items.length > 0) {
      const total = proforma.items.reduce((sum, item) => sum + item.amount, 0);
      const currency = proforma.items[0].currency || 'USD';
      return `${proforma.total_type?.toUpperCase() || 'C&F'} ${total.toLocaleString()} ${currency}`;
    }
    return '-';
  }

  getVehiclesList(proforma: Proforma): string {
    if (proforma.vehicles && proforma.vehicles.length > 0) {
      return proforma.vehicles.map(vehicle =>
        `${vehicle.stock_no || '-'} ${vehicle.make || '-'} ${vehicle.model || '-'} ${vehicle.year || '-'} ${vehicle.chassis_no || '-'}`
      ).join(', ');
    }
    return '-';
  }

  getShippingInfo(proforma: Proforma): any {
    return {
      to: proforma.to_port || '-',
      from: proforma.from_port || '-',
      etd: '-',
      eta: '-',
      ship: '-',
      voyage: '-',
      status: '-',
      company: '-'
    };
  }

  formatDate(dateString: string): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  trackByProformaId(_index: number, proforma: Proforma): string {
    return proforma._id;
  }

  // Filter methods
  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  hasActiveFilters(): boolean {
    return Object.values(this.filters).some(value => value !== '' && value !== null && value !== undefined);
  }

  getActiveFiltersCount(): number {
    return Object.values(this.filters).filter(value => value !== '' && value !== null && value !== undefined).length;
  }

  clearFilters(): void {
    this.filters = {};
    // Clear customer search
    this.clearCustomerSearch();
    // Clear sales person search
    this.clearSalesPersonSearch();
    // Clear port arrays
    this.fromPorts = [];
    this.toPorts = [];
    this.applyFilters();
  }

  applyFilters(): void {
    // Log selected customer and sales person values
    if (this.filters.customer) {
      console.log('Selected Customer Filter:', this.filters.customer);
      this.filters.customer_id = this.filters.customer._id;
    }
    if (this.filters.salesPerson) {
      console.log('Selected Sales Person Filter:', this.filters.salesPerson);
      this.filters.sales_person_id = this.filters.salesPerson._id;
    }

    console.log("filteres-->",this.filters)
    this.loadProformas(1, this.searchTerm, this.filters);
  }

  // Initialize KTMenu instances
  private initializeMenus(): void {
    setTimeout(() => {
      if (typeof KTMenu !== 'undefined') {
        KTMenu.createInstances();
      }
    }, 100);
  }

  // Refresh menus after data load
  private refreshMenus(): void {
    setTimeout(() => {
      if (typeof KTMenu !== 'undefined') {
        KTMenu.createInstances();
      }
    }, 50);
  }

  // Method to open proforma preview modal
  openProformaPreview(proforma: Proforma): void {
    console.log('Opening proforma preview for:', proforma);

    // Set the selected proforma in the service
    this._proformaModalService.setSelectedProforma(proforma);

    // Small delay to ensure data is set
    setTimeout(() => {
      // Open the modal
      const modalElement = document.getElementById('kt_modal_proforma_preview');
      console.log('Modal element found:', modalElement);
      if (modalElement) {
        const modal = new bootstrap.Modal(modalElement);
        modal.show();
      }
    }, 100);
  }

  // Method to open proforma-two preview modal
  openProformaTwoPreview(proforma: Proforma): void {
    console.log('Opening proforma-two preview for:', proforma);

    // Set the selected proforma in the service
    this._proformaModalService.setSelectedProforma(proforma);

    // Small delay to ensure data is set
    setTimeout(() => {
      // Open the proforma-two modal
      const modalElement = document.getElementById('kt_modal_proforma_two_preview');
      console.log('Proforma-two modal element found:', modalElement);
      if (modalElement) {
        const modal = new bootstrap.Modal(modalElement);
        modal.show();
      }
    }, 100);
  }

  SwitchToInvoice(proforma_id: string): void {
    Swal.fire({
      title: 'Switch to Invoice?',
      text: 'Are you sure you want to convert this proforma to an invoice? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, switch to invoice!',
      cancelButtonText: 'No, cancel',
      buttonsStyling: false,
      customClass: {
        confirmButton: 'btn fw-bold btn-primary',
        cancelButton: 'btn fw-bold btn-active-light-primary',
      },
    }).then((result) => {
      if (result.isConfirmed) {
        this.subscriptions.push(
          this._orderService.SwitchToInvoice(proforma_id).subscribe({
            next: (response: any) => {
              if (response.status) {
                this.loadProformas(this.currentPage, this.searchTerm);
                Swal.fire({
                  title: 'Success!',
                  text: 'Proforma has been successfully converted to invoice.',
                  icon: 'success',
                  confirmButtonText: 'Ok, got it!',
                  buttonsStyling: false,
                  customClass: {
                    confirmButton: 'btn btn-primary'
                  }
                });
              }
            },
            error: (error) => {
              console.error('Error switching to invoice:', error);
              Swal.fire({
                title: 'Error!',
                text: 'There was an error converting the proforma to invoice. Please try again.',
                icon: 'error',
                confirmButtonText: 'Ok, got it!',
                buttonsStyling: false,
                customClass: {
                  confirmButton: 'btn btn-primary'
                }
              });
            }
          })
        );
      }
    });
  }

  updateBLStatus(proformaId: string, isOk: boolean): void {
    const statusText = isOk ? 'BL Ok' : 'BL Not Ok';

    // Show confirmation alert
    Swal.fire({
        text: `Are you sure you want to mark this as ${statusText}?`,
        icon: "warning",
        showCancelButton: true,
        buttonsStyling: false,
        confirmButtonText: "Yes, confirm!",
        cancelButtonText: "No, cancel",
        customClass: {
            confirmButton: "btn btn-primary",
            cancelButton: "btn btn-active-light"
        }
    }).then((result: any) => {
        if (result.isConfirmed) {
            this.updateProformaBLStatus(proformaId, isOk);
        }
    });
  }

  // Customer search methods
  private setupCustomerSearchDebounce(): void {
    this.customerSearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchTerm => {
      this.searchCustomers(searchTerm);
    });
  }

  onCustomerSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.customerSearchTerm = target.value;
    this.selectedCustomer = null;
    this.customerSearchSubject.next(this.customerSearchTerm);
    this.showCustomerDropdown = true;
  }

  onCustomerInputFocus(): void {
    if (this.customerSearchResults.length > 0) {
      this.showCustomerDropdown = true;
    }
  }

  onCustomerInputBlur(): void {
    setTimeout(() => {
      this.showCustomerDropdown = false;
    }, 200);
  }

  private searchCustomers(searchTerm: string): void {
    if (!searchTerm || searchTerm.length < 2) {
      this.customerSearchResults = [];
      this.showCustomerDropdown = false;
      return;
    }

    this.isCustomerSearching = true;
    this.subscriptions.push(
      this.clientService.GetData('1', searchTerm).subscribe({
        next: (response: any) => {
          this.isCustomerSearching = false;
          if (response.status && response.data && response.data.data) {
            this.customerSearchResults = response.data.data;
            this.showCustomerDropdown = this.customerSearchResults.length > 0;
          } else {
            this.customerSearchResults = [];
            this.showCustomerDropdown = false;
          }
        },
        error: (error: any) => {
          this.isCustomerSearching = false;
          console.error('Error searching customers:', error);
          this.customerSearchResults = [];
          this.showCustomerDropdown = false;
        }
      })
    );
  }

  selectCustomer(customer: any): void {
    this.selectedCustomer = customer;
    this.customerSearchTerm = this.getCustomerDisplayName(customer);
    this.showCustomerDropdown = false;
    this.customerSearchResults = [];
    this.filters.customer = customer;
  }

  clearCustomerSearch(): void {
    this.customerSearchTerm = '';
    this.selectedCustomer = null;
    this.showCustomerDropdown = false;
    this.customerSearchResults = [];
    this.filters.customer = null;
  }

  getCustomerDisplayName(customer: any): string {
    if (customer.name && customer.company) {
      return `${customer.name}, ${customer.company}, ${customer.currency_type}`;
    }
    return `${customer.name || ''} ${customer.last_name || ''}`.trim() || customer.email || 'Unknown Customer';
  }

  // Sales person search methods
  private setupSalesPersonSearchDebounce(): void {
    this.salesPersonSearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchTerm => {
      this.searchSalesPersons(searchTerm);
    });
  }

  onSalesPersonSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.salesPersonSearchTerm = target.value;
    this.selectedSalesPerson = null;
    this.salesPersonSearchSubject.next(this.salesPersonSearchTerm);
    this.showSalesPersonDropdown = true;
  }

  onSalesPersonInputFocus(): void {
    if (this.salesPersonSearchResults.length > 0) {
      this.showSalesPersonDropdown = true;
    }
  }

  onSalesPersonInputBlur(): void {
    setTimeout(() => {
      this.showSalesPersonDropdown = false;
    }, 200);
  }

  private searchSalesPersons(searchTerm: string): void {
    if (!searchTerm || searchTerm.length < 2) {
      this.salesPersonSearchResults = [];
      this.showSalesPersonDropdown = false;
      return;
    }

    this.isSalesPersonSearching = true;
    this.subscriptions.push(
      this.userService.GetData('1', searchTerm, {is_primary_agent: true }).subscribe({
        next: (response: any) => {
          this.isSalesPersonSearching = false;
          if (response.status && response.data && response.data.data) {
            this.salesPersonSearchResults = response.data.data;
            this.showSalesPersonDropdown = this.salesPersonSearchResults.length > 0;
          } else {
            this.salesPersonSearchResults = [];
            this.showSalesPersonDropdown = false;
          }
        },
        error: (error: any) => {
          this.isSalesPersonSearching = false;
          console.error('Error searching sales persons:', error);
          this.salesPersonSearchResults = [];
          this.showSalesPersonDropdown = false;
        }
      })
    );
  }

  selectSalesPerson(salesPerson: any): void {
    this.selectedSalesPerson = salesPerson;
    this.salesPersonSearchTerm = this.getSalesPersonDisplayName(salesPerson);
    this.showSalesPersonDropdown = false;
    this.salesPersonSearchResults = [];
    this.filters.salesPerson = salesPerson;
  }

  clearSalesPersonSearch(): void {
    this.salesPersonSearchTerm = '';
    this.selectedSalesPerson = null;
    this.showSalesPersonDropdown = false;
    this.salesPersonSearchResults = [];
    this.filters.salesPerson = null;
  }

  getSalesPersonDisplayName(salesPerson: any): string {
    if (salesPerson.name && salesPerson.company) {
      return `${salesPerson.name}, ${salesPerson.company}, ${salesPerson.currency_type}`;
    }
    return `${salesPerson.name || ''} ${salesPerson.last_name || ''}`.trim() || salesPerson.email || 'Unknown Sales Person';
  }

  // New filter methods
  loadCountries(): void {
    this.subscriptions.push(
      this._globalStateService.GetCountries().subscribe({
        next: (countries: any) => {
          this.countries = countries || [];
        },
        error: (error: any) => {
          console.error('Error loading countries:', error);
        }
      })
    );
  }

  loadBanks(): void {
    this.subscriptions.push(
      this.bankService.GetData('1', '', { paginate: false }).subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            this.banks = response.data.data || response.data || [];
          }
        },
        error: (error: any) => {
          console.error('Error loading banks:', error);
        }
      })
    );
  }

  onFromCountryChange(event: any): void {
    const countryCode = event.target.value;
    this.filters.from_country = countryCode;
    this.filters.from_country_label = event.target.options[event.target.selectedIndex].text;
    this.filters.from_port = ''; // Reset port selection
    this.fromPorts = []; // Clear ports

    if (countryCode) {
      this.loadFromPorts(countryCode);
    }
  }

  onToCountryChange(event: any): void {
    const countryCode = event.target.value;
    this.filters.to_country = countryCode;
    this.filters.to_country_label = event.target.options[event.target.selectedIndex].text;
    this.filters.to_port = ''; // Reset port selection
    this.toPorts = []; // Clear ports

    if (countryCode) {
      this.loadToPorts(countryCode);
    }
  }

  loadFromPorts(countryCode: string): void {
    this.isLoadingFromPorts = true;
    this.subscriptions.push(
      this.portService.GetData('1', '', { country_code: countryCode, paginate: false }).subscribe({
        next: (response: any) => {
          this.isLoadingFromPorts = false;
          if (response.status) {
            this.fromPorts = response.data || [];
          } else {
            this.fromPorts = [];
          }
        },
        error: (error: any) => {
          this.isLoadingFromPorts = false;
          console.error('Error loading from ports:', error);
          this.fromPorts = [];
        }
      })
    );
  }

  loadToPorts(countryCode: string): void {
    this.isLoadingToPorts = true;
    this.subscriptions.push(
      this.portService.GetData('1', '', { country_code: countryCode, paginate: false }).subscribe({
        next: (response: any) => {
          this.isLoadingToPorts = false;
          if (response.status) {
            this.toPorts = response.data || [];
          } else {
            this.toPorts = [];
          }
        },
        error: (error: any) => {
          this.isLoadingToPorts = false;
          console.error('Error loading to ports:', error);
          this.toPorts = [];
        }
      })
    );
  }

  // Method to mark proforma as surrendered
  markAsSurrendered(proformaId: string): void {
    Swal.fire({
      title: 'Mark as Surrendered?',
      text: 'Are you sure you want to mark this proforma as surrendered?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, mark as surrendered!',
      cancelButtonText: 'No, cancel',
      buttonsStyling: false,
      customClass: {
        confirmButton: 'btn fw-bold btn-warning',
        cancelButton: 'btn fw-bold btn-active-light-primary',
      },
    }).then((result) => {
      if (result.isConfirmed) {
        this.updateProforma(proformaId, 'surrendered');
      }
    });
  }

  // Method to mark proforma as docs released
  markAsDocsReleased(proforma: Proforma): void {
    // Set the proforma object in window object for the modal to access
    (window as any).selectedProformaForDocsRelease = proforma;

    // Small delay to ensure data is set
    setTimeout(() => {
      const modalElement = document.getElementById('kt_modal_proforma_docs_release');
      if (modalElement) {
        let modal = bootstrap.Modal.getInstance(modalElement);
        if (!modal) {
          modal = new bootstrap.Modal(modalElement);
        }
        modal.show();
      } else {
        console.error('Proforma Docs Release modal element not found');
      }
    }, 100);
  }

  // Method to handle docs release form submission
  onDocsReleaseFormSubmit(formData: any): void {
    console.log('Docs Release Form Submitted:', formData);

    if (formData.proformaId) {
      // Prepare the data to send to API
      const { proformaId, proforma, sameAsConsignee, ...releaseData } = formData;

      const updateData = {
        docs_released: true,
        docs_released_data: releaseData
      };

      // Call the API to update the proforma
      this.subscriptions.push(
        this._orderService.UpdateValue(proformaId, updateData).subscribe({
          next: (response: any) => {
            if (response.status) {
              // Log the message
              console.log(`Proforma ${proformaId} marked as docs_released with data:`, releaseData);

              // Reload the proformas list to reflect changes
              this.loadProformas(this.currentPage, this.searchTerm, this.filters);

              // Show success message
              Swal.fire({
                title: 'Success!',
                text: 'Proforma has been marked as docs released.',
                icon: 'success',
                confirmButtonText: 'Ok, got it!',
                buttonsStyling: false,
                customClass: {
                  confirmButton: 'btn btn-info'
                }
              });
            }
          },
          error: (error) => {
            console.error('Error updating proforma docs_released:', error);

            Swal.fire({
              title: 'Error!',
              text: 'There was an error marking the proforma as docs released. Please try again.',
              icon: 'error',
              confirmButtonText: 'Ok, got it!',
              buttonsStyling: false,
              customClass: {
                confirmButton: 'btn btn-primary'
              }
            });
          }
        })
      );
    }
  }

  // Generic method to update proforma status
  updateProforma(proformaId: string, statusType: 'surrendered' | 'docs_released'): void {
    const updateData: any = {};

    if (statusType === 'surrendered') {
      updateData.surrendered = true;
    } else if (statusType === 'docs_released') {
      updateData.docs_released = true;
    }

    this.subscriptions.push(
      this._orderService.UpdateValue(proformaId, updateData).subscribe({
        next: (response: any) => {
          if (response.status) {
            // Log the message as requested
            console.log(`Proforma ${proformaId} marked as ${statusType}`);

            // Reload the proformas list to reflect changes
            this.loadProformas(this.currentPage, this.searchTerm, this.filters);

            // Show success message
            const statusText = statusType === 'surrendered' ? 'surrendered' : 'docs released';
            const buttonClass = statusType === 'surrendered' ? 'btn-warning' : 'btn-info';

            Swal.fire({
              title: 'Success!',
              text: `Proforma has been marked as ${statusText}.`,
              icon: 'success',
              confirmButtonText: 'Ok, got it!',
              buttonsStyling: false,
              customClass: {
                confirmButton: `btn ${buttonClass}`
              }
            });
          }
        },
        error: (error) => {
          console.error(`Error updating proforma ${statusType}:`, error);
          const statusText = statusType === 'surrendered' ? 'surrendered' : 'docs released';

          Swal.fire({
            title: 'Error!',
            text: `There was an error marking the proforma as ${statusText}. Please try again.`,
            icon: 'error',
            confirmButtonText: 'Ok, got it!',
            buttonsStyling: false,
            customClass: {
              confirmButton: 'btn btn-primary'
            }
          });
        }
      })
    );
  }

  // Method to update BL status
  updateProformaBLStatus(proformaId: string, isOk: boolean): void {
    const updateData = { bl_ok: isOk };

    this.subscriptions.push(
      this._orderService.UpdateValue(proformaId, updateData).subscribe({
        next: (response: any) => {
          if (response.status) {
            // Log the message as requested
            console.log(`Proforma ${proformaId} BL status updated to: ${isOk ? 'OK' : 'Not OK'}`);

            // Reload the proformas list to reflect changes
            this.loadProformas(this.currentPage, this.searchTerm, this.filters);

            // Show success message
            const statusText = isOk ? 'BL OK' : 'BL Not OK';

            Swal.fire({
              title: 'Success!',
              text: `BL status updated to ${statusText}.`,
              icon: 'success',
              confirmButtonText: 'Ok, got it!',
              buttonsStyling: false,
              customClass: {
                confirmButton: 'btn btn-primary'
              }
            });
          }
        },
        error: (error) => {
          console.error(`Error updating BL status:`, error);

          Swal.fire({
            title: 'Error!',
            text: `There was an error updating the BL status. Please try again.`,
            icon: 'error',
            confirmButtonText: 'Ok, got it!',
            buttonsStyling: false,
            customClass: {
              confirmButton: 'btn btn-primary'
            }
          });
        }
      })
    );
  }

  // Method to request shipping
  requestShipping(proformaId: string): void {
    Swal.fire({
      title: 'Request Shipping?',
      text: 'Are you sure you want to request shipping for this proforma?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, request shipping!',
      cancelButtonText: 'No, cancel',
      buttonsStyling: false,
      customClass: {
        confirmButton: 'btn fw-bold btn-primary',
        cancelButton: 'btn fw-bold btn-active-light-primary',
      },
    }).then((result) => {
      if (result.isConfirmed) {
        const updateData = { ship_requested: true };

        this.subscriptions.push(
          this._orderService.UpdateValue(proformaId, updateData).subscribe({
            next: (response: any) => {
              if (response.status) {
                // Log the message as requested
                console.log(`Shipping requested for proforma ${proformaId}`);

                // Reload the proformas list to reflect changes
                this.loadProformas(this.currentPage, this.searchTerm, this.filters);

                // Show success message
                Swal.fire({
                  title: 'Success!',
                  text: 'Shipping has been requested successfully.',
                  icon: 'success',
                  confirmButtonText: 'Ok, got it!',
                  buttonsStyling: false,
                  customClass: {
                    confirmButton: 'btn btn-primary'
                  }
                });
              }
            },
            error: (error) => {
              console.error('Error requesting shipping:', error);

              Swal.fire({
                title: 'Error!',
                text: 'There was an error requesting shipping. Please try again.',
                icon: 'error',
                confirmButtonText: 'Ok, got it!',
                buttonsStyling: false,
                customClass: {
                  confirmButton: 'btn btn-primary'
                }
              });
            }
          })
        );
      }
    });
  }

  // Method to open BL Amend Form modal
  openBlAmendForm(proforma: Proforma, formStage: number): void {
    console.log('Opening BL Amend Form for:', proforma, 'Stage:', formStage);

    // Set the selected data in the service
    this._blAmendFormModalService.setSelectedData(proforma, formStage);

    // Small delay to ensure data is set
    setTimeout(() => {
      const modalElement = document.getElementById('kt_modal_bl_amend_form');
      if (modalElement) {
        let modal = bootstrap.Modal.getInstance(modalElement);
        if (!modal) {
          modal = new bootstrap.Modal(modalElement);
        }
        modal.show();
      } else {
        console.error('BL Amend Form modal element not found');
      }
    }, 100);
  }

  // Method to open Vehicle Detail modal
  openVehicleDetailModal(vehicle: any,proforma_id:string): void {
    console.log('Opening Vehicle Detail modal for vehicle ID:', vehicle._id);

    // Set the selected vehicle ID in window object
    (window as any).selectedVehicleIdForDetail = vehicle._id;
    (window as any).selectedProformaIdForDetail = proforma_id;

    // Small delay to ensure data is set
    setTimeout(() => {
      const modalElement = document.getElementById('kt_modal_proforma_vh_detail');
      if (modalElement) {
        let modal = bootstrap.Modal.getInstance(modalElement);
        if (!modal) {
          modal = new bootstrap.Modal(modalElement);
        }
        modal.show();
      } else {
        console.error('Vehicle Detail modal element not found');
      }
    }, 100);
  }

  getInvoiceBalancePercentage(invoice:any){

    let percentage = (+invoice.paid / +invoice.invoice_amount) * 100;
    percentage = Math.round(percentage);

    return percentage;

  }

  editProforma(proforma: Proforma): void {
    this._router.navigate(['/sales/proforma/edit', proforma._id]);
  }

  showBlButtons(proforma: Proforma): boolean {

    let show = true;

    for(let i = 0; i < proforma.vehicles.length; i++) {
      if(!proforma.vehicles[i].docs) {
        show = false;
        break;
      }
      let hasBlCopy = proforma.vehicles[i].docs.find((doc: any) => doc.type === 'bl_copy');
      if (!hasBlCopy) {
        show = false;
        break;
      }
    }

    console.log({show})
    return show;
  }

}
